import React, { useCallback, useEffect, useRef, useState } from 'react';
import { pickQuote, pickShortQuote } from './poloQuotes';
import PoloImage from '../PoloImage/PoloImage';
import styles from './PoloShowcase.module.css';

const POLO = {
  white: { src: '/assets/white.png', alt: '白色工装 Polo', tone: 'white' },
  black: { src: '/assets/black.png', alt: '黑色工装 Polo', tone: 'black' },
};

const MIN_INTERVAL = 2600;
const MAX_INTERVAL = 4800;
const FADE_MS = 1100;

function rand(min, max) {
  return min + Math.random() * (max - min);
}

function randomInterval() {
  return rand(MIN_INTERVAL, MAX_INTERVAL);
}

function pickTone() {
  return Math.random() < 0.5 ? 'white' : 'black';
}

function pickOppositeTone(tone) {
  return tone === 'white' ? 'black' : 'white';
}

function randomBubbleMetrics(mode = 'single') {
  if (mode === 'single') {
    return {
      bubbleScale: rand(1.0, 1.1),
      fontSize: rand(13, 15.5),
      padX: Math.round(rand(11, 15)),
      padY: Math.round(rand(6, 9)),
    };
  }
  return {
    bubbleScale: 1,
    fontSize: rand(11.5, 13),
    padX: Math.round(rand(10, 14)),
    padY: Math.round(rand(5, 7)),
  };
}

function createBubble(text, slot, enterDelay, mode = 'single') {
  return { text, slot, enterDelay, ...randomBubbleMetrics(mode) };
}

/** 随机场景：数量、颜色顺序、大小、入场先后 */
function randomScene() {
  const isDouble = Math.random() < 0.44;
  const stageScale = rand(0.88, 1.06);

  if (!isDouble) {
    const tone = pickTone();
    return {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      mode: 'single',
      stageScale,
      items: [
        {
          ...POLO[tone],
          slot: 'single',
          scale: rand(0.94, 1.12),
          floatDuration: rand(4.2, 6.2),
          enterDelay: rand(0, 0.2),
        },
      ],
      bubbles: [createBubble(pickQuote(), 'single', rand(0.08, 0.25), 'single')],
    };
  }

  const firstTone = pickTone();
  const secondTone = pickOppositeTone(firstTone);
  const whiteFirst = Math.random() < 0.5;
  const leftTone = whiteFirst ? firstTone : secondTone;
  const rightTone = whiteFirst ? secondTone : firstTone;

  const rightLeft = rand(31, 39);
  const leftLeft = rand(4, 8);
  const leftWidth = rand(52, 60);
  const rightWidth = rand(52, 60);
  const leftFirst = Math.random() < 0.5;

  const leftQuote = pickShortQuote();
  const rightQuote = pickShortQuote(leftQuote);
  const frontSlot = Math.random() < 0.5 ? 'left' : 'right';

  return {
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    mode: 'double',
    stageScale,
    layout: { rightLeft, leftLeft, leftWidth, rightWidth },
    items: [
      {
        ...POLO[leftTone],
        slot: 'left',
        scale: rand(0.88, 1.1),
        floatDuration: rand(4, 6.5),
        enterDelay: leftFirst ? rand(0, 0.18) : rand(0.22, 0.55),
        zIndex: frontSlot === 'left' ? 2 : 1,
      },
      {
        ...POLO[rightTone],
        slot: 'right',
        scale: rand(0.88, 1.1),
        floatDuration: rand(4, 6.5),
        enterDelay: leftFirst ? rand(0.22, 0.55) : rand(0, 0.18),
        zIndex: frontSlot === 'right' ? 2 : 1,
      },
    ],
    bubbles: [
      createBubble(leftQuote, 'left', leftFirst ? rand(0.08, 0.22) : rand(0.22, 0.4), 'double'),
      createBubble(rightQuote, 'right', leftFirst ? rand(0.22, 0.4) : rand(0.08, 0.22), 'double'),
    ],
  };
}

function bubbleMotionStyle(bubble) {
  return {
    '--bubble-scale': bubble.bubbleScale,
    fontSize: `${bubble.fontSize}px`,
    padding: `${bubble.padY}px ${bubble.padX}px`,
    animationDelay: `${bubble.enterDelay}s`,
  };
}

function bubbleLayoutStyle(bubble, scene) {
  if (scene.mode !== 'double') return undefined;

  const { layout } = scene;
  if (bubble.slot === 'left') {
    return { '--bubble-anchor': `${layout.leftLeft + layout.leftWidth / 2}%` };
  }
  return { '--bubble-anchor': `${layout.rightLeft + layout.rightWidth / 2}%` };
}

function poloLayoutStyle(item, scene) {
  if (item.slot === 'single') return undefined;

  const { layout } = scene;
  if (item.slot === 'left') {
    return { left: `${layout.leftLeft}%`, width: `${layout.leftWidth}%` };
  }
  return { left: `${layout.rightLeft}%`, width: `${layout.rightWidth}%` };
}

function poloMotionStyle(item) {
  return {
    '--polo-scale': item.scale,
    '--float-duration': `${item.floatDuration}s`,
    animationDelay: `${item.enterDelay}s`,
    zIndex: item.zIndex,
  };
}

/**
 * 底部工装展示：对话框与玩偶分层，间距可控
 */
export default function PoloShowcase() {
  const [scene, setScene] = useState(() => randomScene());
  const [visible, setVisible] = useState(true);
  const timerRef = useRef(null);
  const fadeRef = useRef(null);

  const nextScene = useCallback(() => {
    setVisible(false);
    fadeRef.current = setTimeout(() => {
      setScene(randomScene());
      setVisible(true);
    }, FADE_MS * 0.45);
  }, []);

  useEffect(() => {
    const tick = () => {
      nextScene();
      timerRef.current = setTimeout(tick, randomInterval());
    };

    timerRef.current = setTimeout(tick, randomInterval());
    return () => {
      clearTimeout(timerRef.current);
      clearTimeout(fadeRef.current);
    };
  }, [nextScene]);

  const stageClass = [
    styles.stage,
    scene.mode === 'double' ? styles.stageDouble : styles.stageSingle,
    visible ? styles.stageVisible : styles.stageHidden,
  ].join(' ');

  const stageInnerClass = [
    styles.stageInner,
    visible ? styles.stageInnerVisible : styles.stageInnerHidden,
  ].join(' ');

  const bubbleLayerClass = [
    styles.bubbleLayer,
    scene.mode === 'double' ? styles.bubbleLayerDouble : styles.bubbleLayerSingle,
  ].join(' ');

  const stageStyle = {
    transitionDuration: `${FADE_MS}ms`,
  };

  const stageInnerStyle = {
    transform: `scale(${scene.stageScale})`,
  };

  return (
    <aside className={styles.wrap} aria-label="工装款式预览">
      <div className={styles.glow} aria-hidden="true" />
      <div key={scene.id} className={stageClass} style={stageStyle}>
        <div className={stageInnerClass} style={stageInnerStyle}>
          <div className={bubbleLayerClass}>
            {scene.bubbles.map((bubble) => (
              <div
                key={`${scene.id}-${bubble.slot}`}
                className={[
                  styles.bubble,
                  styles[`bubble${bubble.slot.charAt(0).toUpperCase()}${bubble.slot.slice(1)}`],
                  scene.mode === 'double' && styles.bubbleAnchored,
                  visible ? styles.bubbleVisible : styles.bubbleHidden,
                ].join(' ')}
                style={{
                  ...bubbleMotionStyle(bubble),
                  ...bubbleLayoutStyle(bubble, scene),
                }}
              >
                {bubble.text}
              </div>
            ))}
          </div>
          <div className={styles.poloLayer}>
            {scene.items.map((item) => (
              <PoloImage
                key={`${scene.id}-${item.slot}-${item.tone}`}
                src={item.src}
                alt={item.alt}
                className={[
                  styles.polo,
                  styles[`polo${item.slot.charAt(0).toUpperCase()}${item.slot.slice(1)}`],
                  visible ? styles.poloActive : styles.poloIdle,
                ].join(' ')}
                style={{
                  ...poloMotionStyle(item),
                  ...poloLayoutStyle(item, scene),
                  transitionDuration: `${FADE_MS}ms`,
                }}
                draggable={false}
                loading="lazy"
              />
            ))}
          </div>
        </div>
      </div>
    </aside>
  );
}
